<template>
  <div class="ident" :style="styleForApp" :class="{ cafexBackground: needsDefaultIdent }">
    <div class="overlay" :class="{ externalTenant: isExternalTenantApp }">
      <div class="tenantLabel">
        <template v-if="isExternalTenantApp">
          from
          <span class="name" :title="tenantName(app.tenantId, tenants, true)">
            {{ tenantName(app.tenantId, tenants) }}
          </span>
        </template>
      </div>
      <div v-if="showAppName" class="appName">{{ app.name }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef } from 'vue';

import Tenant from '@/data/datatypes/Tenant';
import { patternForApp } from '@/data/helpers/TaskHelper';
import { AppIdentDetails, MiniApp } from '@/data/tasks/MiniApp';

interface Props {
  userTenantId?: string;
  app: MiniApp | AppIdentDetails;
  tenants: Record<string, Tenant>;
  showAppName?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  showAppName: false,
});

const needsDefaultIdent: ComputedRef<boolean> = computed(() => {
  return !('pattern' in props.app);
});

const styleForApp: ComputedRef<Record<string, string> | undefined> = computed(() => {
  // The check for 'id' is to separate the behaviour for the 2 classes of psudo-app;
  // the old collaborate app and the psudo-apps in the calendar. It's possible for the
  // MiniApp to have an 'id' but not a 'pattern' E.g. the internal collab app, in which case
  // a pattern is derived from the object's properties.
  if ('id' in props.app || 'pattern' in props.app) {
    return {
      background: patternForApp(props.app as MiniApp).gradient
    };
  }
  return undefined;
});

const isExternalTenantApp: ComputedRef<boolean> = computed(() => {
  return !!props.app.tenantId && !!props.userTenantId && props.app.tenantId !== props.userTenantId;
});

function tenantName(tenantId: string, tenants: Record<string, Tenant>, verbose: boolean = false): string {
  const tenantName: string = tenants[tenantId]?.name ?? 'External tenant';
  return verbose ? `This App is shared from another tenant:\n${tenantName}` : tenantName;
}
</script>

<style scoped lang="scss">
.ident {
  position: relative;
  aspect-ratio: 1;
  justify-content: flex-start;
  color: $colour-text-contrast;

  .overlay {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    &.externalTenant {
      background: $external-tenant-app-ident-overlay-gradient;
      background-blend-mode: multiply;
      position: absolute;
      top: 0;
      left: 0;
    }

    .tenantLabel {
      margin: 0.75rem 1rem 0.75rem 2.5rem;
      overflow: hidden;
      text-overflow: ellipsis;
      box-sizing: border-box;
      text-align: right;
      white-space: nowrap;
      .name {
        font-weight: $weight-semi-bold;
      }
    }

    .appName {
      align-items: center;
      padding-left: 1.25rem;
      font-size: $size-font-header;
      font-weight: $weight-semi-bold;
      flex-basis: 50%;
      display: flex;
      align-items: center;
    }
  }

  &.cafexBackground {
    background-image: url('~@/assets/images/cafex-non-app-background.png');
    background-repeat: no-repeat;
    background-size: cover;
  }
}
</style>
